import React, { useState, useEffect } from "react";
import {
  User,
  Mail,
  Phone,
  FileText,
  CreditCard,
  AlertCircle,
  Building,
  ShieldCheck,
  Landmark,
  Briefcase,
} from "lucide-react";
import {
  validateCPF,
  validatePhone,
  validateFullName,
  validateRG,
  maskCPF,
  maskPhone,
} from "../../../shared/utils/validators";
import { TextInput } from "../../../shared/components/TextInput";
import { AutocompleteSelect } from "../../../shared/components/AutocompleteSelect";
import type { UserRole } from "../../../shared/types";
import { useRoleLabels } from "../../../shared/hooks/useRoleLabels";

const maskCNPJ = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};

/**
 * Campos com validação "ao vivo" (conforme o usuário digita). Somente estes são
 * gerenciados pelo efeito de validação — os demais erros vêm do submit e precisam
 * sobreviver aos re-renders.
 */
const LIVE_VALIDATED_FIELDS = ["name", "cpf", "rg", "email", "phone"] as const;

const EMPLOYMENT_TYPE_OPTIONS = [
  { value: "clt", label: "CLT" },
  { value: "pj", label: "PJ" },
];

const ACCOUNT_TYPE_OPTIONS = [
  { value: "corrente", label: "Conta Corrente" },
  { value: "poupança", label: "Conta Poupança" },
  { value: "pagamento", label: "Conta Pagamento" },
];

interface UserFormData {
  name: string;
  cpf: string;
  rg: string;
  email: string;
  phone: string;
  role: UserRole | "";
  companyId: string;
  managerId: string;
  supplierId: string;
  /** Regime de contratação — obrigatório apenas para o perfil Colaborador. */
  employmentType: "clt" | "pj" | "";
  bankName: string;
  bankAgency: string;
  bankAccount: string;
  bankAccountType: string;
  bankPixKey: string;
  pjCompanyName: string;
  pjCnpj: string;
  pjTradeName: string;
}

interface Company {
  id: string;
  corporateName: string;
  tradeName: string;
}

interface Manager {
  id: string;
  name: string;
  companyId: string | null;
}

interface SupplierOption {
  id: string;
  name: string;
  documentNumber: string;
}

interface UserRegistrationFormProps {
  onSubmit: (data: UserFormData) => Promise<void>;
  companies: Company[];
  managers?: Manager[];
  suppliers?: SupplierOption[];
  corporateDomains?: string[];
}

export const UserRegistrationForm: React.FC<UserRegistrationFormProps> = ({
  onSubmit,
  companies,
  managers = [],
  suppliers = [],
  corporateDomains = [],
}) => {
  const { options: roles } = useRoleLabels();
  const roleOptions = roles.map((r) => ({ value: r.slug, label: r.name }));
  const [formData, setFormData] = useState<UserFormData>({
    name: "",
    cpf: "",
    rg: "",
    email: "",
    phone: "",
    role: "",
    companyId: "",
    managerId: "",
    supplierId: "",
    employmentType: "",
    bankName: "",
    bankAgency: "",
    bankAccount: "",
    bankAccountType: "",
    bankPixKey: "",
    pjCompanyName: "",
    pjCnpj: "",
    pjTradeName: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const errorSummaryRef = React.useRef<HTMLDivElement>(null);

  const companyOptions = companies.map((c) => ({
    value: c.id,
    label: c.tradeName || c.corporateName,
  }));

  const managerOptions = managers
    .filter((m) => !formData.companyId || m.companyId === formData.companyId)
    .map((m) => ({ value: m.id, label: m.name }));
  const supplierOptions = suppliers.map((s) => ({
    value: s.id,
    label: `${s.name} · ${s.documentNumber}`,
  }));

  const needsCompany =
    formData.role === "collaborator" || formData.role === "manager";
  const needsManager = formData.role === "collaborator";
  const needsSupplier = formData.role === "supplier";
  const isCollaborator = formData.role === "collaborator";
  // Dados de empresa só existem no regime PJ; CLT informa apenas dados bancários.
  const isCollaboratorPj = isCollaborator && formData.employmentType === "pj";

  useEffect(() => {
    const liveErrors: Record<string, string> = {};
    if (touched.name && formData.name && !validateFullName(formData.name))
      liveErrors.name = "Nome deve conter pelo menos nome e sobrenome";
    if (touched.cpf && formData.cpf) {
      const cleanCPF = formData.cpf.replace(/[^\d]/g, "");
      if (cleanCPF.length === 11 && !validateCPF(formData.cpf))
        liveErrors.cpf = "CPF inválido. Verifique os dígitos verificadores";
    }
    if (touched.rg && formData.rg && !validateRG(formData.rg))
      liveErrors.rg = "RG deve conter pelo menos 5 caracteres";
    if (
      touched.email &&
      formData.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    )
      liveErrors.email = "E-mail inválido";
    if (touched.phone && formData.phone) {
      const cleanPhone = formData.phone.replace(/[^\d]/g, "");
      if (cleanPhone.length === 11 && !validatePhone(formData.phone))
        liveErrors.phone = "Telefone inválido. Formato: (00) 9 0000-0000";
    }

    setErrors((prev) => {
      const next = { ...prev };
      for (const field of LIVE_VALIDATED_FIELDS) {
        if (liveErrors[field]) next[field] = liveErrors[field];
        else if (touched[field]) delete next[field];
      }
      return next;
    });
  }, [formData, touched]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = "Nome completo é obrigatório";
    else if (!validateFullName(formData.name))
      newErrors.name = "Nome deve conter pelo menos nome e sobrenome";
    if (!formData.cpf.trim()) newErrors.cpf = "CPF é obrigatório";
    else if (!validateCPF(formData.cpf))
      newErrors.cpf = "CPF inválido. Verifique os dígitos verificadores";
    if (!formData.rg.trim()) newErrors.rg = "RG é obrigatório";
    else if (!validateRG(formData.rg))
      newErrors.rg = "RG deve conter pelo menos 5 caracteres";
    if (!formData.email.trim()) newErrors.email = "E-mail é obrigatório";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "E-mail inválido";
    if (!formData.phone.trim()) newErrors.phone = "Telefone é obrigatório";
    else if (!validatePhone(formData.phone))
      newErrors.phone = "Telefone inválido. Formato: (00) 9 0000-0000";
    if (!formData.role) newErrors.role = "Perfil é obrigatório";
    if (formData.role === "manager" && !formData.companyId)
      newErrors.companyId = "Empresa é obrigatória para o perfil Gestor";
    if (formData.role === "collaborator" && !formData.companyId)
      newErrors.companyId = "Empresa é obrigatória para o perfil Colaborador";
    if (formData.role === "collaborator" && !formData.managerId)
      newErrors.managerId = "Gestor é obrigatório para o perfil Colaborador";
    if (formData.role === "supplier" && !formData.supplierId)
      newErrors.supplierId =
        "Fornecedor vinculado é obrigatório para o perfil Fornecedor";
    if (isCollaborator) {
      if (!formData.employmentType)
        newErrors.employmentType = "Regime de contratação é obrigatório";
      // Dados bancários são exigidos nos dois regimes.
      if (!formData.bankName.trim()) newErrors.bankName = "Banco é obrigatório";
      if (!formData.bankAgency.trim())
        newErrors.bankAgency = "Agência é obrigatória";
      if (!formData.bankAccount.trim())
        newErrors.bankAccount = "Conta é obrigatória";
      if (!formData.bankAccountType)
        newErrors.bankAccountType = "Tipo de conta é obrigatório";
    }
    if (isCollaboratorPj) {
      if (!formData.pjCompanyName.trim())
        newErrors.pjCompanyName = "Razão Social PJ é obrigatória";
      if (!formData.pjCnpj.trim()) newErrors.pjCnpj = "CNPJ PJ é obrigatório";
      else if (formData.pjCnpj.replace(/\D/g, "").length !== 14)
        newErrors.pjCnpj = "CNPJ deve ter 14 dígitos";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      // Sem isto, um erro em campo fora da área visível do modal faz o clique
      // parecer que "não fez nada".
      errorSummaryRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    setIsLoading(true);
    try {
      const fixedFormData = {
        ...formData,
        companyId: formData.companyId === "" ? null : formData.companyId,
        managerId: formData.managerId === "" ? null : formData.managerId,
        supplierId: formData.supplierId === "" ? null : formData.supplierId,
        bankName: formData.bankName || undefined,
        bankAgency: formData.bankAgency || undefined,
        bankAccount: formData.bankAccount || undefined,
        bankAccountType: formData.bankAccountType || undefined,
        bankPixKey: formData.bankPixKey || undefined,
        employmentType: formData.employmentType || undefined,
        // Campos PJ só são enviados no regime PJ.
        pjCompanyName: isCollaboratorPj
          ? formData.pjCompanyName || undefined
          : undefined,
        pjCnpj:
          isCollaboratorPj && formData.pjCnpj
            ? formData.pjCnpj.replace(/\D/g, "")
            : undefined,
        pjTradeName: isCollaboratorPj
          ? formData.pjTradeName || undefined
          : undefined,
      };
      await onSubmit(fixedFormData);
    } catch (error: any) {
      let errorMessage = "Erro ao registrar usuário. Tente novamente.";
      // Axios expõe o corpo da resposta em `response.data` (não é um Response do fetch).
      const data = error?.response?.data;
      if (data) {
        // O ValidationPipe do Nest devolve `message` como array de mensagens.
        if (Array.isArray(data.message)) errorMessage = data.message.join("; ");
        else if (data.message) errorMessage = data.message;
        else if (data.error) errorMessage = data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      console.error("Falha ao cadastrar usuário:", error);
      setErrors({ submit: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof UserFormData, value: string) => {
    let processedValue = value;
    if (field === "cpf") processedValue = maskCPF(value);
    else if (field === "phone") processedValue = maskPhone(value);
    else if (field === "pjCnpj") processedValue = maskCNPJ(value);

    setFormData((prev) => {
      const next = { ...prev, [field]: processedValue };
      if (field === "role" && !["collaborator", "manager"].includes(value)) {
        next.companyId = "";
        next.managerId = "";
      }
      if (field === "role" && value !== "collaborator") {
        next.managerId = "";
        next.employmentType = "";
        next.bankName = "";
        next.bankAgency = "";
        next.bankAccount = "";
        next.bankAccountType = "";
        next.bankPixKey = "";
        next.pjCompanyName = "";
        next.pjCnpj = "";
        next.pjTradeName = "";
      }
      // Ao migrar para CLT, os dados de empresa deixam de ser aplicáveis.
      if (field === "employmentType" && value !== "pj") {
        next.pjCompanyName = "";
        next.pjCnpj = "";
        next.pjTradeName = "";
      }
      if (field === "role" && value !== "supplier") {
        next.supplierId = "";
      }
      if (field === "companyId") {
        next.managerId = "";
      }
      return next;
    });
  };

  const handleBlur = (field: keyof UserFormData) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // `submit` é o erro vindo da API e já tem seu próprio bloco no rodapé.
  const blockingErrors = Object.entries(errors).filter(
    ([field, message]) => field !== "submit" && !!message,
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div ref={errorSummaryRef}>
        {blockingErrors.length > 0 && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm font-semibold text-red-700 flex items-center mb-2">
              <AlertCircle className="w-4 h-4 mr-2" />
              Corrija os campos abaixo para continuar:
            </p>
            <ul className="list-disc list-inside space-y-1">
              {blockingErrors.map(([field, message]) => (
                <li key={field} className="text-sm text-red-600">
                  {message}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Nome */}
      <TextInput
        label="Nome Completo *"
        type="text"
        icon={<User className="w-5 h-5" />}
        value={formData.name}
        onChange={(e) => handleInputChange("name", e.target.value)}
        onBlur={() => handleBlur("name")}
        error={errors.name}
      />

      {/* CPF | RG */}
      <div className="grid grid-cols-2 gap-4">
        <TextInput
          label="CPF *"
          type="text"
          icon={<CreditCard className="w-5 h-5" />}
          value={formData.cpf}
          onChange={(e) => handleInputChange("cpf", e.target.value)}
          onBlur={() => handleBlur("cpf")}
          error={errors.cpf}
          maxLength={14}
        />
        <TextInput
          label="RG *"
          type="text"
          icon={<FileText className="w-5 h-5" />}
          value={formData.rg}
          onChange={(e) => handleInputChange("rg", e.target.value)}
          onBlur={() => handleBlur("rg")}
          error={errors.rg}
        />
      </div>

      {/* E-mail | Telefone */}
      <div className="grid grid-cols-2 gap-4">
        <TextInput
          label="E-mail *"
          type="email"
          icon={<Mail className="w-5 h-5" />}
          value={formData.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
          onBlur={() => handleBlur("email")}
          error={errors.email}
        />
        <TextInput
          label="Telefone *"
          type="text"
          icon={<Phone className="w-5 h-5" />}
          value={formData.phone}
          onChange={(e) => handleInputChange("phone", e.target.value)}
          onBlur={() => handleBlur("phone")}
          error={errors.phone}
          maxLength={16}
        />
      </div>

      {/* Perfil */}
      <AutocompleteSelect
        label="Perfil *"
        icon={<ShieldCheck className="w-5 h-5" />}
        options={roleOptions}
        value={formData.role}
        onChange={(val) => handleInputChange("role", val)}
        error={errors.role}
      />

      {/* Empresa | Gestor */}
      {needsCompany && (
        <div className="grid grid-cols-2 gap-4">
          <AutocompleteSelect
            label="Empresa *"
            icon={<Building className="w-5 h-5" />}
            options={companyOptions}
            value={formData.companyId}
            onChange={(val) => handleInputChange("companyId", val)}
            error={errors.companyId}
          />
          {needsManager && (
            <AutocompleteSelect
              label="Gestor *"
              icon={<User className="w-5 h-5" />}
              options={managerOptions}
              value={formData.managerId}
              onChange={(val) => handleInputChange("managerId", val)}
              error={errors.managerId}
            />
          )}
        </div>
      )}

      {/* Regime de contratação (somente colaborador) */}
      {isCollaborator && (
        <fieldset className="space-y-2 pt-2 border-t border-gray-100">
          <legend className="text-sm font-semibold text-gray-700">
            Regime de Contratação *
          </legend>
          <div className="flex gap-6">
            {EMPLOYMENT_TYPE_OPTIONS.map((option) => (
              <label
                key={option.value}
                className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
              >
                <input
                  type="radio"
                  name="employmentType"
                  value={option.value}
                  checked={formData.employmentType === option.value}
                  onChange={(e) =>
                    handleInputChange("employmentType", e.target.value)
                  }
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                {option.label}
              </label>
            ))}
          </div>
          {errors.employmentType && (
            <p className="flex items-center gap-1 text-sm text-red-600">
              <AlertCircle className="w-4 h-4" />
              {errors.employmentType}
            </p>
          )}
        </fieldset>
      )}

      {/* Dados bancários (somente colaborador) */}
      {isCollaborator && (
        <div className="space-y-4 pt-2 border-t border-gray-100">
          <p className="text-sm font-semibold text-gray-700">Dados Bancários</p>

          {/* Banco | Agência */}
          <div className="grid grid-cols-2 gap-4">
            <TextInput
              label="Banco *"
              icon={<Landmark className="w-5 h-5" />}
              value={formData.bankName}
              onChange={(e) => handleInputChange("bankName", e.target.value)}
              error={errors.bankName}
            />
            <TextInput
              label="Agência *"
              value={formData.bankAgency}
              onChange={(e) => handleInputChange("bankAgency", e.target.value)}
              error={errors.bankAgency}
            />
          </div>

          {/* Conta | Tipo */}
          <div className="grid grid-cols-2 gap-4">
            <TextInput
              label="Conta *"
              value={formData.bankAccount}
              onChange={(e) => handleInputChange("bankAccount", e.target.value)}
              error={errors.bankAccount}
            />
            <AutocompleteSelect
              label="Tipo de Conta *"
              options={ACCOUNT_TYPE_OPTIONS}
              value={formData.bankAccountType}
              onChange={(val) => handleInputChange("bankAccountType", val)}
              error={errors.bankAccountType}
            />
          </div>

          {/* Pix */}
          <TextInput
            label="Chave Pix (opcional)"
            value={formData.bankPixKey}
            onChange={(e) => handleInputChange("bankPixKey", e.target.value)}
          />
        </div>
      )}

      {/* Dados PJ (somente colaborador no regime PJ) */}
      {isCollaboratorPj && (
        <div className="space-y-4 pt-2 border-t border-gray-100">
          <p className="text-sm font-semibold text-gray-700">Dados PJ</p>

          {/* Razão Social PJ | CNPJ PJ */}
          <div className="grid grid-cols-2 gap-4">
            <TextInput
              label="Razão Social PJ *"
              icon={<Briefcase className="w-5 h-5" />}
              value={formData.pjCompanyName}
              onChange={(e) =>
                handleInputChange("pjCompanyName", e.target.value)
              }
              onBlur={() => handleBlur("pjCompanyName")}
              error={errors.pjCompanyName}
            />
            <TextInput
              label="CNPJ PJ *"
              icon={<FileText className="w-5 h-5" />}
              value={formData.pjCnpj}
              onChange={(e) => handleInputChange("pjCnpj", e.target.value)}
              onBlur={() => handleBlur("pjCnpj")}
              error={errors.pjCnpj}
              maxLength={18}
            />
          </div>

          {/* Nome Fantasia PJ */}
          <TextInput
            label="Nome Fantasia PJ (opcional)"
            icon={<Briefcase className="w-5 h-5" />}
            value={formData.pjTradeName}
            onChange={(e) => handleInputChange("pjTradeName", e.target.value)}
          />
        </div>
      )}

      {needsSupplier && (
        <AutocompleteSelect
          label="Fornecedor vinculado *"
          icon={<Building className="w-5 h-5" />}
          options={supplierOptions}
          value={formData.supplierId}
          onChange={(val) => handleInputChange("supplierId", val)}
          error={errors.supplierId}
        />
      )}

      {errors.submit && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600 flex items-center">
            <AlertCircle className="w-4 h-4 mr-2" />
            {errors.submit}
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading}
        className={`w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        {isLoading ? "Cadastrando..." : "Cadastrar Usuário"}
      </button>
    </form>
  );
};
